/* STEP / IGES / BREP for /step-viewer: what the file is, what its units are,
 * and the reader's answer turned into something a page can draw.
 *
 * Runs in the reading worker (step-worker.js) and in Node (the judge and the
 * unit tests in tests/js/step-viewer-mesh.test.mjs), so it touches neither the
 * DOM nor three.js: typed arrays in, typed arrays out.
 *
 * The reader is occt-import-js (OpenCascade compiled to WebAssembly, LGPL-2.1,
 * shipped unmodified in public/vendor/occt-import-js/). Its answer is a tree of
 * nodes and a flat list of meshes in plain JS arrays, already in WORLD
 * coordinates (instances are baked, one mesh per solid per occurrence) and in
 * the unit we asked for — always millimetres here, so that every number on the
 * page and the STL export share one space. The unit the FILE was written in is
 * read separately (fileUnit) and used only to SHOW numbers.
 */
(function (root, factory) {
  if (typeof module === 'object' && module.exports) module.exports = factory()
  else root.StepMesh = factory()
})(typeof globalThis !== 'undefined' ? globalThis : typeof self !== 'undefined' ? self : this, function () {
  'use strict'

  var MM_PER = { mm: 1, cm: 10, m: 1000, in: 25.4, ft: 304.8, um: 0.001 }

  function ascii(u8, from, to) {
    var s = ''
    var end = Math.min(u8.length, to)
    for (var i = from; i < end; i++) s += String.fromCharCode(u8[i])
    return s
  }

  /* ---------------------------------------------------------------- sniff --
     The first bytes decide, not the name: a part saved as .txt or downloaded
     without an extension opens the same; a .stp that is really a ZIP from a
     model library is unpacked by the worker. Native files of CAD programs are
     named by EXTENSION only — their bytes say "OLE2" or "ZIP", which would send
     the person to an archive tool that cannot help either. */
  var NATIVE = {
    m3d: 'kompas', a3d: 'kompas', cdw: 'kompas', frw: 'kompas', spw: 'kompas',
    sldprt: 'solidworks', sldasm: 'solidworks', slddrw: 'solidworks',
    ipt: 'inventor', iam: 'inventor', idw: 'inventor',
    f3d: 'fusion', f3z: 'fusion',
    catpart: 'catia', catproduct: 'catia', catdrawing: 'catia',
    prt: 'nx_creo', asm: 'nx_creo',
    par: 'solidedge', psm: 'solidedge',
    x_t: 'parasolid', x_b: 'parasolid', xmt_txt: 'parasolid',
    '3dm': 'rhino', fcstd: 'freecad', skp: 'sketchup',
    stl: 'mesh', obj: 'mesh', '3mf': 'mesh',
    dwg: 'drawing', dxf: 'drawing',
    jt: 'other', ifc: 'other', '3dxml': 'other', sat: 'other', sab: 'other',
  }

  function extOf(name) {
    var m = /\.([a-z0-9_]+)(?:\.\d+)?$/i.exec(String(name || ''))
    return m ? m[1].toLowerCase() : ''
  }

  function sniff(u8, name) {
    var ext = extOf(name)
    if (!u8 || !u8.length) return { kind: 'empty', ext: ext }
    if (u8[0] === 0x1f && u8[1] === 0x8b) return { kind: 'gzip', ext: ext }
    if (u8[0] === 0x50 && u8[1] === 0x4b && (u8[2] === 3 || u8[2] === 5) ) {
      return NATIVE[ext] ? { kind: 'native', app: NATIVE[ext], ext: ext } : { kind: 'zip', ext: ext }
    }
    var head = ascii(u8, 0, 4096)
    if (head.charCodeAt(0) === 0xEF && head.charCodeAt(1) === 0xBB && head.charCodeAt(2) === 0xBF) head = head.slice(3) /* UTF-8 BOM, read byte by byte */
    var lead = head.replace(/^[\s\0]+/, '')
    if (/^ISO-10303-21\s*;/.test(lead)) return { kind: 'step', ext: ext }
    if (/^<\?xml/.test(lead) && /10303|iso_10303_28|stepx/i.test(head)) return { kind: 'stepxml', ext: ext }
    if (/^DBRep_DrawableShape|^CASCADE Topology V/.test(lead)) return { kind: 'brep', ext: ext }
    if (isIges(head)) return { kind: 'iges', ext: ext }
    if (NATIVE[ext]) return { kind: 'native', app: NATIVE[ext], ext: ext }
    if (/^solid\s/i.test(lead) && /facet\s+normal/i.test(head)) return { kind: 'native', app: 'mesh', ext: ext || 'stl' }
    return { kind: 'unknown', ext: ext }
  }

  /* IGES is 80-column card images; column 73 names the section (S, G, D, P,
     T). A file without a Start section begins with G. Line ends vary (CRLF,
     LF, none at all in some exporters), so the check reads the 73rd column of
     the first record however the records are separated. */
  function isIges(head) {
    var first = head.split(/\r\n|\n|\r/)[0]
    if (first.length >= 73 && first.length <= 82 && /[SG]/.test(first.charAt(72)) && /^[ \d]{7}$/.test(first.substring(73, 80))) return true
    if (head.length >= 160 && /[SG]/.test(head.charAt(72)) && /^[ \d]{7}$/.test(head.substring(73, 80)) && /[SG]/.test(head.charAt(152))) return true
    return false
  }

  /* ------------------------------------------------------------ STEP unit --
     The length unit of the GEOMETRY is the one the geometric representation
     context assigns (GLOBAL_UNIT_ASSIGNED_CONTEXT). A file usually names other
     length units too — PMI in inches on a millimetre part (NIST CTC-03), a
     tolerance unit — so "the first LENGTH_UNIT in the file" is wrong; the
     contexts are counted and the unit most of them assign wins. */
  /* A definition is "#123=" (or "#123 =") — a reference is never followed by
     "=", and "#" anchors the number's start, so a plain indexOf finds exactly
     the record. A regular expression per lookup re-scanned a 200-MB assembly
     from the top; a scan that skipped references ran out of tries on files
     where #11938 has hundreds of longer namesakes (#119380…, NIST STC-10). */
  function stepEntity(text, id) {
    var cache = text === _entText ? _entCache : (_entText = text, _entCache = {})
    if (Object.prototype.hasOwnProperty.call(cache, id)) return cache[id]
    var at = text.indexOf('#' + id + '=')
    var k = at < 0 ? -1 : at + String(id).length + 1
    if (at < 0) {
      var m = new RegExp('#' + id + '[ \t]+=').exec(text)
      if (m) k = m.index + m[0].length
    }
    var body = null
    if (k >= 0) {
      var end = text.indexOf(';', k)
      body = text.slice(k, end < 0 ? Math.min(text.length, k + 2000) : end)
    }
    cache[id] = body
    return body
  }
  var _entText = null
  var _entCache = {}

  function unitOfEntity(text, body, depth) {
    if (!body || depth > 3) return null
    if (!/LENGTH_UNIT/i.test(body)) return null
    var cb = /CONVERSION_BASED_UNIT\s*\(\s*'([^']*)'/i.exec(body)
    if (cb) {
      var n = cb[1].toLowerCase().replace(/\s+/g, '')
      if (/^(inch|inches|in)$/.test(n)) return 'in'
      if (/^(foot|feet|ft)$/.test(n)) return 'ft'
      if (/^(millimet(re|er)s?|mm)$/.test(n)) return 'mm'
      if (/^(centimet(re|er)s?|cm)$/.test(n)) return 'cm'
      if (/^(met(re|er)s?|m)$/.test(n)) return 'm'
      /* an odd name: follow the conversion factor's own unit */
      var ref = /CONVERSION_BASED_UNIT\s*\(\s*'[^']*'\s*,\s*#(\d+)/i.exec(body)
      if (ref) {
        var mwu = stepEntity(text, ref[1])
        var f = mwu && /\(\s*([-\d.Ee+]+)\s*\)?\s*,\s*#(\d+)/.exec(mwu)
        var factor = mwu && /MEASURE_WITH_UNIT\s*\(\s*(?:[A-Z_]+\s*\(\s*)?([-\d.Ee+]+)/i.exec(mwu)
        var base = f ? unitOfEntity(text, stepEntity(text, f[2]), depth + 1) : null
        if (factor && base) {
          var mm = parseFloat(factor[1]) * MM_PER[base]
          if (Math.abs(mm - 25.4) < 1e-6) return 'in'
          if (Math.abs(mm - 304.8) < 1e-4) return 'ft'
        }
      }
      return null
    }
    var si = /SI_UNIT\s*\(\s*(\$|\.[A-Z]+\.)\s*,\s*\.METRE\.\s*\)/i.exec(body)
    if (si) {
      var p = si[1].toUpperCase()
      if (p === '$') return 'm'
      if (p === '.MILLI.') return 'mm'
      if (p === '.CENTI.') return 'cm'
      if (p === '.MICRO.') return 'um'
      if (p === '.DECI.') return 'cm' /* never seen; shown in cm rather than lie in mm */
      if (p === '.KILO.') return 'm'
    }
    return null
  }

  function stepUnit(text) {
    var votes = {}
    var re = /GLOBAL_UNIT_ASSIGNED_CONTEXT\s*\(\s*\(([^)]*)\)/gi
    var m
    var seen = 0
    while ((m = re.exec(text)) && seen < 200) {
      seen++
      var ids = m[1].match(/#\d+/g) || []
      for (var i = 0; i < ids.length; i++) {
        var u = unitOfEntity(text, stepEntity(text, ids[i].slice(1)), 0)
        if (u) { votes[u] = (votes[u] || 0) + 1; break }
      }
    }
    var best = null
    for (var k in votes) if (!best || votes[k] > votes[best]) best = k
    return best
  }

  /* An assembly may keep its parts in OTHER files (AP214/AP242 external
     references: DOCUMENT_FILE('TAIL.stp', …) — CAx-IF S1). Such a file alone
     has no geometry; the page names the files it needs. */
  function stepExternalFiles(text) {
    var out = []
    var re = /DOCUMENT_FILE\s*\(\s*'([^']{1,120})'/gi
    var m
    while ((m = re.exec(text)) && out.length < 12) {
      var n = m[1].split(/[\\/]/).pop()
      if (/\.(step|stp|p21|stpz|igs|iges)$/i.test(n) && out.indexOf(n) < 0) out.push(n)
    }
    return out
  }

  /* What the header says: the application protocol and who wrote the file. */
  function stepHeader(text) {
    var head = text.slice(0, 20000)
    var out = { schema: null, system: null }
    var fs = /FILE_SCHEMA\s*\(\s*\(\s*'([^']*)'/i.exec(head)
    if (fs) {
      var s = fs[1].toUpperCase()
      out.schema = /AP242|MANAGED_MODEL_BASED_3D_ENGINEERING/.test(s) ? 'AP242'
        : /AUTOMOTIVE_DESIGN|AP214/.test(s) ? 'AP214'
        : /CONFIG_CONTROL_DESIGN|AP203/.test(s) ? 'AP203'
        : s.slice(0, 40)
    }
    var fn = /FILE_NAME\s*\(([\s\S]*?)\)\s*;/i.exec(head)
    if (fn) {
      /* FILE_NAME(name, time, (author), (org), preprocessor, originating_system, authorisation) */
      var strs = []
      var sre = /'((?:[^']|'')*)'/g
      var sm
      while ((sm = sre.exec(fn[1]))) strs.push(sm[1].replace(/''/g, "'"))
      var cands = strs.slice(-3).concat(strs.slice(2))
      for (var i = 0; i < cands.length; i++) {
        var c = cands[i].trim()
        if (c && /[a-z]/i.test(c) && !/^\d{4}-\d\d-\d\d/.test(c) && c.length < 120 && !/^(none|unknown|\$)$/i.test(c)) {
          if (/solidworks|kompas|компас|inventor|fusion|catia|nx|creo|pro\/?e|freecad|onshape|solid ?edge|rhino|open ?cascade|siemens|autodesk|dassault|ptc|ascon|аскон|t-flex|nanocad|zw3d|cadquery|step|translator|processor|exporter|tech soft|hoops|spatial|parasolid/i.test(c)) { out.system = c; break }
        }
      }
    }
    return out
  }

  /* ------------------------------------------------------------ IGES unit --
     Global section, parameter 14: 1 inch, 2 mm, 3 named in parameter 15,
     4 foot, 5 mile, 6 metre, 7 km, 8 mil, 9 micron, 10 cm, 11 microinch.
     Parameters are separated by the delimiter the file itself declares in
     parameter 1 (default comma), strings are Hollerith (nH…). */
  function igesGlobal(text) {
    var lines = text.split(/\r\n|\n|\r/)
    var g = ''
    for (var i = 0; i < lines.length && i < 400; i++) {
      var L = lines[i]
      if (L.length >= 73 && L.charAt(72) === 'G') g += L.substring(0, 72)
      else if (g && L.length >= 73 && L.charAt(72) !== 'G') break
    }
    if (!g) return null
    var pd = ','
    var rd = ';'
    var m = /^\s*1H(.)/.exec(g)
    if (m) pd = m[1]
    var params = []
    var cur = ''
    var j = 0
    while (j < g.length) {
      var hm = /^\s*(\d+)H/.exec(g.slice(j, j + 12))
      if (hm && cur.trim() === '') {
        var n = parseInt(hm[1], 10)
        var start = j + hm[0].length
        cur = g.substr(start, n)
        j = start + n
        continue
      }
      var ch = g.charAt(j)
      if (ch === pd || ch === rd) {
        params.push(cur.trim())
        cur = ''
        j++
        if (ch === rd) break
        if (params.length === 1 && params[0] !== '' && params[0].length === 1) { /* delimiter itself */ }
        continue
      }
      cur += ch
      j++
    }
    if (cur.trim()) params.push(cur.trim())
    if (params.length === 1 && params[0].length === 1) rd = ';'
    return params
  }

  var IGES_UNIT = { 1: 'in', 2: 'mm', 4: 'ft', 6: 'm', 9: 'um', 10: 'cm' }

  function igesInfo(text) {
    var p = igesGlobal(text)
    var out = { unit: null, system: null }
    if (!p) return out
    /* params[0] = parameter delimiter, [1] = record delimiter, [2] product id
       sender, [3] file name, [4] native system id, [5] preprocessor version,
       ..., [13] unit flag, [14] unit name */
    var flag = parseInt(p[13], 10)
    if (IGES_UNIT[flag]) out.unit = IGES_UNIT[flag]
    else if (flag === 3) {
      var nm = String(p[14] || '').toUpperCase()
      out.unit = /^IN|INCH/.test(nm) ? 'in' : /^MM/.test(nm) ? 'mm' : /^FT/.test(nm) ? 'ft' : /^M$/.test(nm) ? 'm' : /^CM/.test(nm) ? 'cm' : null
    }
    var sys = String(p[4] || '').trim()
    if (sys && /[a-z]/i.test(sys) && sys.length < 120) out.system = sys
    return out
  }

  /* One call for the worker: format-specific unit and header facts. Reads the
     text in one pass over at most the first and the needed parts of the file;
     units are near the start in every exporter seen, but a context may sit at
     the end of a big assembly, so STEP reads the whole thing (latin1, one byte
     per char). */
  function fileFacts(u8, kind) {
    if (kind === 'step') {
      /* The first 8 MB hold the header and, in every exporter seen, the first
         part's units; the whole file is read only when they do not. */
      var head = latin1(u8.subarray(0, Math.min(u8.length, 8 << 20)))
      var h = stepHeader(head)
      var unit = stepUnit(head)
      if (!unit && u8.length > head.length) unit = stepUnit(latin1(u8))
      return { unit: unit, schema: h.schema, system: h.system, extRefs: stepExternalFiles(head) }
    }
    if (kind === 'iges') {
      var t = latin1(u8.subarray(0, Math.min(u8.length, 200000)))
      var i = igesInfo(t)
      return { unit: i.unit, schema: null, system: i.system }
    }
    return { unit: null, schema: null, system: null }
  }

  function latin1(u8) {
    if (typeof TextDecoder !== 'undefined') {
      try { return new TextDecoder('latin1').decode(u8) } catch (_) {}
    }
    var s = ''
    for (var i = 0; i < u8.length; i += 0x8000) s += String.fromCharCode.apply(null, u8.subarray(i, i + 0x8000))
    return s
  }

  /* ------------------------------------------------------------- convert --
     The reader's answer → parts the page draws. One part = one mesh of the
     reader = one solid (or shell, or loose faces) of one occurrence. */

  function nextPow2(n) { var p = 1; while (p < n) p <<= 1; return p }

  /* Vertices of neighbouring faces sit on the same spot but are separate
     entries (each face is triangulated on its own). Welding them by position
     is what lets the edge pass tell "the boundary between two faces" (a B-rep
     edge, drawn) from "two triangles inside one face" (not drawn), and makes a
     closed solid's triangles close. Exact matches are the rule — both faces
     take their boundary points from the same edge discretisation — so the grid
     is fine: a millionth of the part's size. */
  function weld(pos, bbox) {
    var n = pos.length / 3
    var ids = new Uint32Array(n)
    var dx = bbox[3] - bbox[0], dy = bbox[4] - bbox[1], dz = bbox[5] - bbox[2]
    var diag = Math.sqrt(dx * dx + dy * dy + dz * dz) || 1
    var q = 1 / (diag * 1e-6)
    var cap = nextPow2(Math.max(16, n * 2))
    var mask = cap - 1
    var table = new Int32Array(cap).fill(-1)
    var qx = new Int32Array(n), qy = new Int32Array(n), qz = new Int32Array(n)
    var uniq = 0
    for (var i = 0; i < n; i++) {
      var ix = Math.round((pos[i * 3] - bbox[0]) * q)
      var iy = Math.round((pos[i * 3 + 1] - bbox[1]) * q)
      var iz = Math.round((pos[i * 3 + 2] - bbox[2]) * q)
      var h = (Math.imul(ix, 73856093) ^ Math.imul(iy, 19349663) ^ Math.imul(iz, 83492791)) & mask
      for (;;) {
        var j = table[h]
        if (j === -1) {
          table[h] = uniq; qx[uniq] = ix; qy[uniq] = iy; qz[uniq] = iz
          ids[i] = uniq++
          break
        }
        if (qx[j] === ix && qy[j] === iy && qz[j] === iz) { ids[i] = j; break }
        h = (h + 1) & mask
      }
    }
    return { ids: ids, count: uniq }
  }

  /* B-rep edges from the triangles: a triangle side shared by two DIFFERENT
     faces is an edge of the model; a side used once is the border of an open
     surface (drawn too, and counted — a solid with open sides is not closed,
     so its volume is not shown). */
  function edgesOf(pos, idx, faceRanges, bbox) {
    var w = weld(pos, bbox)
    var ids = w.ids
    var N = w.count
    var map = new Map()
    var segs = []
    var nf = faceRanges.length / 2
    for (var f = 0; f < nf; f++) {
      var t0 = faceRanges[f * 2], t1 = faceRanges[f * 2 + 1]
      for (var t = t0; t <= t1; t++) {
        for (var e = 0; e < 3; e++) {
          var va = idx[t * 3 + e], vb = idx[t * 3 + (e + 1) % 3]
          var a = ids[va], b = ids[vb]
          if (a === b) continue
          var key = a < b ? a * N + b : b * N + a
          var prev = map.get(key)
          if (prev === undefined) map.set(key, f)
          else if (prev !== -1) {
            if (prev !== f) segs.push(va, vb)
            map.set(key, -1)
          }
        }
      }
    }
    var open = 0
    var raw = null
    map.forEach(function (v, key) {
      if (v === -1) return
      open++
      if (!raw) {
        /* the first raw vertex of each welded id — only open edges need it */
        raw = new Int32Array(N).fill(-1)
        for (var i = 0; i < ids.length; i++) if (raw[ids[i]] === -1) raw[ids[i]] = i
      }
      var a = Math.floor(key / N), b = key - a * N
      segs.push(raw[a], raw[b])
    })
    var out = new Float32Array(segs.length * 3)
    for (var s = 0; s < segs.length; s++) {
      var v = segs[s]
      out[s * 3] = pos[v * 3]; out[s * 3 + 1] = pos[v * 3 + 1]; out[s * 3 + 2] = pos[v * 3 + 2]
    }
    return { lines: out, open: open }
  }

  function colorKey(c) { return c ? Math.round(c[0] * 255) + ',' + Math.round(c[1] * 255) + ',' + Math.round(c[2] * 255) : '' }

  function convertMesh(m) {
    var P = m.attributes.position.array
    var pos = new Float32Array(P.length)
    var bbox = [Infinity, Infinity, Infinity, -Infinity, -Infinity, -Infinity]
    for (var i = 0; i < P.length; i += 3) {
      var x = P[i], y = P[i + 1], z = P[i + 2]
      pos[i] = x; pos[i + 1] = y; pos[i + 2] = z
      if (x < bbox[0]) bbox[0] = x; if (x > bbox[3]) bbox[3] = x
      if (y < bbox[1]) bbox[1] = y; if (y > bbox[4]) bbox[4] = y
      if (z < bbox[2]) bbox[2] = z; if (z > bbox[5]) bbox[5] = z
    }
    var N = m.attributes.normal ? m.attributes.normal.array : null
    var nrm = N && N.length === P.length ? Float32Array.from(N) : null
    var I = m.index.array
    var idx = new Uint32Array(I)
    var bf = m.brep_faces || []
    var faceRanges = new Uint32Array(bf.length * 2)
    var untessellated = 0
    /* colour groups: consecutive faces of one colour become one draw range */
    var palette = []
    var paletteIndex = {}
    var groups = []
    var meshColor = m.color || null
    for (var f = 0; f < bf.length; f++) {
      var face = bf[f]
      faceRanges[f * 2] = face.first
      faceRanges[f * 2 + 1] = face.last
      if (face.last < face.first) { untessellated++; continue }
      var c = face.color || meshColor
      var k = colorKey(c)
      var ci = paletteIndex[k]
      if (ci === undefined) { ci = paletteIndex[k] = palette.length; palette.push(c ? [c[0], c[1], c[2]] : null) }
      var last = groups.length ? groups[groups.length - 1] : null
      if (last && last[2] === ci && last[0] + last[1] === face.first) last[1] += face.last - face.first + 1
      else groups.push([face.first, face.last - face.first + 1, ci])
    }
    if (!bf.length && idx.length) { groups.push([0, idx.length / 3, 0]); palette.push(meshColor ? meshColor.slice(0, 3) : null); faceRanges = new Uint32Array([0, idx.length / 3 - 1]) }
    var g = new Uint32Array(groups.length * 3)
    for (var gi = 0; gi < groups.length; gi++) { g[gi * 3] = groups[gi][0]; g[gi * 3 + 1] = groups[gi][1]; g[gi * 3 + 2] = groups[gi][2] }
    var ed = idx.length ? edgesOf(pos, idx, faceRanges, bbox) : { lines: new Float32Array(0), open: 0 }
    var vol = 0, area = 0
    for (var t = 0; t < idx.length; t += 3) {
      var a = idx[t] * 3, b = idx[t + 1] * 3, cc = idx[t + 2] * 3
      var ax = pos[a], ay = pos[a + 1], az = pos[a + 2]
      var bx = pos[b], by = pos[b + 1], bz = pos[b + 2]
      var cx = pos[cc], cy = pos[cc + 1], cz = pos[cc + 2]
      vol += (ax * (by * cz - bz * cy) - ay * (bx * cz - bz * cx) + az * (bx * cy - by * cx)) / 6
      var ux = bx - ax, uy = by - ay, uz = bz - az, vx = cx - ax, vy = cy - ay, vz = cz - az
      var nx = uy * vz - uz * vy, ny = uz * vx - ux * vz, nz = ux * vy - uy * vx
      area += Math.sqrt(nx * nx + ny * ny + nz * nz) / 2
    }
    return {
      name: String(m.name || ''),
      pos: pos,
      nrm: nrm,
      idx: idx,
      groups: g,
      palette: palette,
      edges: ed.lines,
      open: ed.open,
      faces: bf.length,
      untessellated: untessellated,
      bbox: bbox,
      volume: Math.abs(vol),
      area: area,
      tris: idx.length / 3,
    }
  }

  /* The tree the person sees: the reader's nodes, with each mesh as a leaf.
     A node without a name takes its only mesh's name; one-mesh nodes are not
     split into "node → mesh" (that is one thing to the person). */
  function convert(res, opts) {
    opts = opts || {}
    var meshes = res.meshes || []
    var parts = new Array(meshes.length)
    var budget = opts.onProgress || null
    for (var i = 0; i < meshes.length; i++) {
      parts[i] = convertMesh(meshes[i])
      meshes[i] = null /* the reader's arrays are the biggest thing alive — let them go */
      if (budget && (i & 15) === 15) budget(i + 1, meshes.length)
    }
    function node(n) {
      var kids = []
      var nm = n.meshes || []
      var ch = n.children || []
      for (var c = 0; c < ch.length; c++) {
        var k = node(ch[c])
        if (k) kids.push(k)
      }
      var name = String(n.name || '')
      if (nm.length === 1 && !kids.length) return { name: name || parts[nm[0]].name, part: nm[0] }
      for (var j = 0; j < nm.length; j++) kids.push({ name: parts[nm[j]].name, part: nm[j] })
      if (!kids.length) return null
      if (kids.length === 1 && !name) return kids[0]
      return { name: name, children: kids }
    }
    var tree = res.root ? node(res.root) : null
    if (!tree) tree = { name: '', children: [] }
    if (tree.part !== undefined) tree = { name: '', children: [tree] }
    var transfer = []
    var stats = { parts: parts.length, tris: 0, faces: 0, untessellated: 0, open: 0 }
    for (var p = 0; p < parts.length; p++) {
      var pt = parts[p]
      stats.tris += pt.tris; stats.faces += pt.faces; stats.untessellated += pt.untessellated
      if (pt.open) stats.open++
      transfer.push(pt.pos.buffer, pt.idx.buffer, pt.groups.buffer, pt.edges.buffer)
      if (pt.nrm) transfer.push(pt.nrm.buffer)
    }
    return { tree: tree, parts: parts, stats: stats, transfer: transfer }
  }

  /* How finely curved faces are split into triangles. The reader's default
     (chord 0.1 % of the model's average size, 0.5 rad) read a Ø2-inch cylinder
     as 1.998 in and the 63-mm NIST FTC-11 plate as 62.95 mm, and drew small
     holes as 13-gons; half the chord and 0.35 rad (20°) read both exactly and
     cost 1.3–2.6× the triangles on the corpus, with the same reading time
     (measured 2026-11-05). Big files keep the default: there the memory of the
     tab, not the look of a hole, decides whether the model opens at all. */
  function meshParams(bytes) {
    if (bytes > 30 * 1048576) return { linearUnit: 'millimeter' }
    return { linearUnit: 'millimeter', linearDeflectionType: 'bounding_box_ratio', linearDeflection: 0.0005, angularDeflection: 0.35 }
  }

  /* ----------------------------------------------------------------- STL --
     Binary STL in millimetres (slicers assume mm; the file has no unit field).
     `items` = [{ pos, idx }] in world coordinates. Facet normals are computed
     from the winding, as every slicer recomputes them anyway.
     Triangles with two corners on one point (OpenCascade puts them at cone
     apexes and sphere poles) are left out: they have no area, and with them a
     closed solid does not read as closed — trimesh called 6 of 11 NIST solids
     "not watertight" for 4–14 such triangles each; without them, all closed. */
  function degenerate(P, a, b, c) {
    return (P[a] === P[b] && P[a + 1] === P[b + 1] && P[a + 2] === P[b + 2]) ||
      (P[b] === P[c] && P[b + 1] === P[c + 1] && P[b + 2] === P[c + 2]) ||
      (P[a] === P[c] && P[a + 1] === P[c + 1] && P[a + 2] === P[c + 2])
  }

  function stlBytes(items, header) {
    var tris = 0
    var k, t, P, I
    for (k = 0; k < items.length; k++) {
      P = items[k].pos; I = items[k].idx
      for (t = 0; t < I.length; t += 3) if (!degenerate(P, I[t] * 3, I[t + 1] * 3, I[t + 2] * 3)) tris++
    }
    var buf = new ArrayBuffer(84 + tris * 50)
    var dv = new DataView(buf)
    var h = String(header || 'binary STL, millimetres').slice(0, 79)
    for (var c = 0; c < h.length; c++) dv.setUint8(c, h.charCodeAt(c) & 0x7f)
    dv.setUint32(80, tris, true)
    var o = 84
    for (k = 0; k < items.length; k++) {
      P = items[k].pos; I = items[k].idx
      for (t = 0; t < I.length; t += 3) {
        var a = I[t] * 3, b = I[t + 1] * 3, cc = I[t + 2] * 3
        if (degenerate(P, a, b, cc)) continue
        var ux = P[b] - P[a], uy = P[b + 1] - P[a + 1], uz = P[b + 2] - P[a + 2]
        var vx = P[cc] - P[a], vy = P[cc + 1] - P[a + 1], vz = P[cc + 2] - P[a + 2]
        var nx = uy * vz - uz * vy, ny = uz * vx - ux * vz, nz = ux * vy - uy * vx
        var l = Math.sqrt(nx * nx + ny * ny + nz * nz) || 1
        dv.setFloat32(o, nx / l, true); dv.setFloat32(o + 4, ny / l, true); dv.setFloat32(o + 8, nz / l, true)
        o += 12
        dv.setFloat32(o, P[a], true); dv.setFloat32(o + 4, P[a + 1], true); dv.setFloat32(o + 8, P[a + 2], true)
        dv.setFloat32(o + 12, P[b], true); dv.setFloat32(o + 16, P[b + 1], true); dv.setFloat32(o + 20, P[b + 2], true)
        dv.setFloat32(o + 24, P[cc], true); dv.setFloat32(o + 28, P[cc + 1], true); dv.setFloat32(o + 32, P[cc + 2], true)
        o += 36
        dv.setUint16(o, 0, true)
        o += 2
      }
    }
    return new Uint8Array(buf)
  }

  return {
    MM_PER: MM_PER,
    meshParams: meshParams,
    sniff: sniff,
    extOf: extOf,
    stepUnit: stepUnit,
    stepHeader: stepHeader,
    igesInfo: igesInfo,
    fileFacts: fileFacts,
    convert: convert,
    convertMesh: convertMesh,
    weld: weld,
    stlBytes: stlBytes,
  }
})
